// Keyboard shortcut modifier keys
export type ModifierKey = "meta" | "shift" | "alt" | "ctrl";

// Keyboard shortcut definition
export interface KeyboardShortcut {
  /** The main key character (e.g., "d", "Delete", "Enter", " " for space) */
  key?: string;
  /** The physical key code (e.g., "Digit1", "KeyA"). Use when modifiers would alter the character. */
  code?: string;
  /** Required modifier keys */
  modifiers?: ModifierKey[];
  /** Human-readable display string (e.g., "⇧⌘C"). Auto-generated if not provided. */
  displayString?: string;
}

/**
 * Detect if the user is on a Mac platform.
 */
export function isMacPlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  // Use modern userAgentData API if available, fallback to userAgent string
  const platform =
    (navigator as Navigator & { userAgentData?: { platform: string } }).userAgentData?.platform ||
    navigator.userAgent;
  return /mac|iPod|iPhone|iPad/i.test(platform);
}

/**
 * Generate display string for keyboard shortcut.
 * Automatically detects platform to show Mac or Windows style shortcuts.
 */
export function getShortcutDisplayString(shortcut: KeyboardShortcut): string {
  const isMac = isMacPlatform();

  // Only use displayString if it's explicitly provided AND we're on Mac
  // (since displayString values are typically Mac-style symbols)
  if (shortcut.displayString && isMac) return shortcut.displayString;

  const parts: string[] = [];

  if (isMac) {
    // Mac style: use symbols, order is Ctrl, Alt, Shift, Cmd
    if (shortcut.modifiers?.includes("ctrl")) parts.push("⌃");
    if (shortcut.modifiers?.includes("alt")) parts.push("⌥");
    if (shortcut.modifiers?.includes("shift")) parts.push("⇧");
    if (shortcut.modifiers?.includes("meta")) parts.push("⌘");
  } else {
    // Windows style: use text, order is Ctrl, Alt, Shift
    // Note: "meta" maps to Ctrl on Windows
    if (shortcut.modifiers?.includes("ctrl") || shortcut.modifiers?.includes("meta")) {
      parts.push("Ctrl+");
    }
    if (shortcut.modifiers?.includes("alt")) parts.push("Alt+");
    if (shortcut.modifiers?.includes("shift")) parts.push("Shift+");
  }

  // Add the main key - use key if available, otherwise derive from code
  const keyForDisplay = shortcut.key ?? getKeyFromCode(shortcut.code);
  const keyDisplay = getKeyDisplayString(keyForDisplay ?? "", isMac);
  parts.push(keyDisplay);

  return parts.join("");
}

/**
 * Convert a key code to a display-friendly key character.
 */
function getKeyFromCode(code: string | undefined): string | undefined {
  if (!code) return undefined;

  // Handle digit keys
  if (code.startsWith("Digit")) {
    return code.replace("Digit", "");
  }

  // Handle letter keys
  if (code.startsWith("Key")) {
    return code.replace("Key", "").toLowerCase();
  }

  // Handle other common codes
  const codeMap: Record<string, string> = {
    Space: " ",
    Backspace: "Backspace",
    Delete: "Delete",
    Enter: "Enter",
    Escape: "Escape",
    Tab: "Tab",
    ArrowUp: "ArrowUp",
    ArrowDown: "ArrowDown",
    ArrowLeft: "ArrowLeft",
    ArrowRight: "ArrowRight",
    Equal: "=",
    Minus: "-",
    BracketLeft: "[",
    BracketRight: "]",
    Backslash: "\\",
    Semicolon: ";",
    Quote: "'",
    Comma: ",",
    Period: ".",
    Slash: "/",
    Backquote: "`",
  };

  return codeMap[code] ?? code;
}

/**
 * Get display string for a single key.
 */
function getKeyDisplayString(key: string, isMac: boolean): string {
  if (isMac) {
    const macKeyMap: Record<string, string> = {
      " ": "Space",
      Backspace: "⌫",
      Delete: "⌦",
      Enter: "↵",
      Escape: "⎋",
      Tab: "⇥",
      ArrowUp: "↑",
      ArrowDown: "↓",
      ArrowLeft: "←",
      ArrowRight: "→",
      "=": "+",
      "-": "-",
    };
    return macKeyMap[key] || key.toUpperCase();
  } else {
    const windowsKeyMap: Record<string, string> = {
      " ": "Space",
      Backspace: "Backspace",
      Delete: "Del",
      Enter: "Enter",
      Escape: "Esc",
      Tab: "Tab",
      ArrowUp: "↑",
      ArrowDown: "↓",
      ArrowLeft: "←",
      ArrowRight: "→",
      "=": "+",
      "-": "-",
    };
    return windowsKeyMap[key] || key.toUpperCase();
  }
}
